import { useCallback, useEffect, useState } from 'react';
import { getLang } from '../i18n';

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Sprache für Texte, die der Server baut (Ampel-Wörter, Ziel-Sätze, Wrapped …); ohne Angabe antwortet er deutsch.
  const res = await fetch(path, { ...init, headers: { Accept: 'application/json', 'Accept-Language': getLang(), ...init?.headers } });
  if (res.status === 401) {
    // Sitzung fehlt oder ist abgelaufen: zum Login und danach hierher zurück.
    window.location.assign(`/auth/login?next=${encodeURIComponent(location.pathname + location.search)}`);
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg = ((await res.json()) as { error?: string }).error ?? msg;
    } catch {
      /* kein JSON */
    }
    throw new ApiError(msg, res.status);
  }
  return (await res.json()) as T;
}

/** Lädt einmal und bei `reload()`; hält Daten, Fehler und Ladezustand. */
export function useApi<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    setLoading(true);
    api<T>(path)
      .then((d) => alive && (setData(d), setError(null)))
      .catch((e: Error) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [path, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}
